import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import jwtDecode from 'jwt-decode';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthPermission, AuthPermissions } from 'skipify-types';
import { useSearchParams } from 'react-router-dom';

import {
  useLazyGetMerchantQuery,
  useLazyGetMerchantsByUserQuery,
  useLazyGetPublicMerchantQuery,
} from '../store/modules/merchant/api';
import configureAuth from '../api/configureAuth';
import type { DecodedJwtPayload } from '../util/auth0Utils';
import { getAccessTokenWithAudience } from '../util/auth0Utils';
import {
  GetMerchantsByUserParams,
  GetMerchantsByUserQuery,
  MerchantAccess,
} from '../store/modules/merchant/types';
import type { PaginatedResponse } from '../store/modules/types';
import { CONFIG_MERCHANT_LIST_PAGE_SIZE, IndexPath, tabs } from '../util/constants';
import Logger from '../util/logger';
import { useUpdateLDContext } from '../hooks/useUpdateLDContext';

const CHILD_ONLY_LIST_PAGE_SIZE = 50;

function dedupeMerchantAccess(merchants: MerchantAccess[]): MerchantAccess[] {
  return merchants.filter(
    (m, i, self) => self.findIndex((t) => t.merchantId === m.merchantId) === i,
  );
}

/**
 * Prefer live `window` query on bootstrap — React `searchParams` can lag one frame (new tab / Login).
 * Accept any non-empty id string (not only UUID) so deep links match backend ids.
 */
function readMerchantIdFromWindowSearch(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('merchantId')?.trim();
  if (!raw || raw.length > 128) return null;
  return raw;
}

/** JWT permission check aligned with isChildMerchantOnlyUser (no React state). */
export function isChildMerchantOnlyPermissionSet(
  permissions: AuthPermission[] | undefined | null,
): boolean {
  if (!permissions?.length) return false;
  if (permissions.some((p) => p.startsWith(AuthPermissions.ADMIN_ALL))) return false;
  return (
    permissions.includes(AuthPermissions.UPDATE_CHILD_MERCHANTS_ONLY) &&
    !permissions.includes(AuthPermissions.UPDATE_MERCHANTS)
  );
}

/**
 * Child-only breadcrumb navigability: API lineage is near-current first; crumbs render reversed
 * (root left). Clicks allowed from the leftmost id in merchantAccess/parentMerchants through current.
 */
export function isChildOnlyLineageMerchantNavigable(
  merchantIds: readonly string[],
  lineageMerchantId: string,
  merchantAccess: MerchantAccess[] | null,
  parentMerchants: MerchantAccess[] | null,
): boolean {
  const k = merchantIds.indexOf(lineageMerchantId);
  if (k === -1) return false;

  const accessibleIds = new Set<string>([
    ...(merchantAccess ?? []).map((m) => m.merchantId),
    ...(parentMerchants ?? []).map((p) => p.merchantId),
  ]);

  const displayOrder = [...merchantIds].reverse();
  let shallowAccessibleDisplayIndex = displayOrder.length;
  for (let i = 0; i < displayOrder.length; i++) {
    if (accessibleIds.has(displayOrder[i])) {
      shallowAccessibleDisplayIndex = i;
      break;
    }
  }
  if (shallowAccessibleDisplayIndex === displayOrder.length) {
    return false;
  }

  const displayIdx = merchantIds.length - 1 - k;
  return displayIdx >= shallowAccessibleDisplayIndex;
}

/** Skipify-wide roles can navigate to any merchant in the hierarchy breadcrumb. */
export function hasSkipifyWideLineageNavigateAccess(
  permissions: AuthPermission[] | undefined | null,
): boolean {
  return Boolean(
    permissions?.some(
      (p) => p.startsWith(AuthPermissions.ADMIN_ALL) || p.startsWith(AuthPermissions.READ_ALL),
    ),
  );
}

/**
 * Merchant-scoped users may only navigate to merchants explicitly granted via accountToShop
 * (merchantAccess). Ancestors shown for context stay disabled when not in that list.
 */
export function isMerchantScopedLineageMerchantNavigable(
  lineageMerchantId: string,
  merchantAccess: MerchantAccess[] | null,
  userPermissions?: AuthPermission[] | undefined | null,
): boolean {
  if (hasSkipifyWideLineageNavigateAccess(userPermissions)) {
    return true;
  }
  return Boolean(merchantAccess?.some((m) => m.merchantId === lineageMerchantId));
}

/** True when `merchantId` is one of the child-only multi-parent picker rows. */
export function isMerchantIdParentPickerRow(
  merchantId: string | null | undefined,
  parentMerchants: MerchantAccess[] | null | undefined,
): boolean {
  return Boolean(merchantId && parentMerchants?.some((p) => p.merchantId === merchantId));
}

// Core permission checking logic (moved here to avoid circular dependency)
export const SKIPIFY_VIEWER_PERMISSIONS: readonly AuthPermission[] = [
  AuthPermissions.READ_PAYLINKS,
  AuthPermissions.READ_PAYLINK_TRANSACTIONS,
  AuthPermissions.READ_PAYLINK_CUSTOMERS,
  AuthPermissions.READ_MERCHANTS,
  AuthPermissions.READ_MIT,
  AuthPermissions.READ_OWN_MIT,
];

export const MERCHANT_PERMISSIONS: readonly AuthPermission[] = [
  AuthPermissions.CREATE_MERCHANTS,
  AuthPermissions.READ_MERCHANTS,
  AuthPermissions.UPDATE_MERCHANTS,
  AuthPermissions.DELETE_MERCHANTS,
  AuthPermissions.UPDATE_CHILD_MERCHANTS_ONLY,
];

export const INSIGHTS_PERMISSIONS: readonly AuthPermission[] = [
  AuthPermissions.READ_MERCHANT_INSIGHTS,
];

export const AGENT_HUB_PERMISSIONS: readonly AuthPermission[] = [
  AuthPermissions.READ_AGENT_HUB,
  AuthPermissions.UPDATE_AGENT_HUB,
];

export const PAYLINK_PERMISSIONS: readonly AuthPermission[] = [
  AuthPermissions.CANCEL_PAYLINKS,
  AuthPermissions.CREATE_PAYLINKS,
  AuthPermissions.CREATE_PAYLINK_CUSTOMERS,
  AuthPermissions.DELETE_PAYLINK_CUSTOMERS,
  AuthPermissions.READ_PAYLINK_CUSTOMERS,
  AuthPermissions.UPDATE_PAYLINK_CUSTOMERS,
  AuthPermissions.READ_PAYLINKS,
  AuthPermissions.REFUND_PAYLINKS,
  AuthPermissions.UPDATE_PAYLINKS,
  AuthPermissions.DOWNLOAD_PAYLINKS,
  AuthPermissions.READ_PAYLINK_TRANSACTIONS,
  AuthPermissions.READ_OWN_PAYLINK_CUSTOMERS,
  AuthPermissions.UPDATE_OWN_PAYLINK_CUSTOMERS,
  AuthPermissions.DELETE_OWN_PAYLINK_CUSTOMERS,
  AuthPermissions.CREATE_OWN_PAYLINKS,
  AuthPermissions.READ_OWN_PAYLINKS,
  AuthPermissions.UPDATE_OWN_PAYLINKS,
  AuthPermissions.CANCEL_OWN_PAYLINKS,
  AuthPermissions.REFUND_OWN_PAYLINKS,
  AuthPermissions.READ_OWN_PAYLINK_TRANSACTIONS,
];

export const CARD_ON_FILE_PERMISSIONS: readonly AuthPermission[] = [
  AuthPermissions.READ_MIT,
  AuthPermissions.CHARGE_MIT,
  AuthPermissions.REFUND_MIT,
  AuthPermissions.READ_OWN_MIT,
  AuthPermissions.CHARGE_OWN_MIT,
  AuthPermissions.REFUND_OWN_MIT,
];

const checkCanAccess = (feature: AuthPermission, permissions: AuthPermission[]): boolean => {
  // Return false if permissions are not provided or empty
  if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  // Check if the user has admin permissions (full access)
  if (permissions.some((p) => p.startsWith(AuthPermissions.ADMIN_ALL))) {
    return true;
  }

  // Check if the user has the specific permission for this feature
  if (permissions.includes(feature)) {
    return true;
  }

  // If the user has read permissions, check if they can access viewer-only features
  const hasReadAccess = permissions.some(
    (p) => p.startsWith(AuthPermissions.ADMIN_ALL) || p.startsWith(AuthPermissions.READ_ALL),
  );
  if (hasReadAccess && (SKIPIFY_VIEWER_PERMISSIONS as readonly string[]).includes(feature)) {
    return true;
  }

  return false;
};

export type LineageItem = {
  name: string;
  merchantId: string;
  hasAccess: boolean;
};

export type MerchantContextState = {
  merchantName: string;
  merchantUserId: string | null;
  emailAddress: string | null;
  permissions: AuthPermission[] | null;
  isLoading: boolean;
  error: Error | null;
  merchantAccess: MerchantAccess[] | null;
  /** Result of onlyParentMerchants list call; persisted for parent picker and breadcrumbs */
  parentMerchants: MerchantAccess[] | null;
  /** Total parent count from paginated API (child-only); may exceed parentMerchants.length */
  parentMerchantsTotalRecords: number | null;
  /** When true, child-only user with multiple parents must pick a parent (or go back to picker) */
  requiresParentSelection: boolean;
  /**
   * After Continue on the multi-parent grid: true until back to parent list. Used so Configuration
   * shows child-merchant landing even when the chosen row is not a DB root (`partnerMerchantId` set).
   */
  pendingChildMerchantLandingAfterParentPick: boolean;
  directLineage: LineageItem[] | null;
  unauthorizedMerchantId: boolean;
  partnerMerchantId: string | null | undefined;
  /** True when the current merchant has at least one non-deleted child merchant (from GET merchant). */
  hasDescendants: boolean;
};

const defaultState: MerchantContextState = {
  merchantName: '',
  merchantUserId: null,
  emailAddress: null,
  permissions: null,
  isLoading: false,
  error: null,
  merchantAccess: null,
  parentMerchants: null,
  parentMerchantsTotalRecords: null,
  requiresParentSelection: false,
  pendingChildMerchantLandingAfterParentPick: false,
  directLineage: null,
  unauthorizedMerchantId: false,
  partnerMerchantId: undefined,
  hasDescendants: false,
};

type MerchantContextValue = MerchantContextState & {
  merchantId: string;
  logout: () => void;
  setMerchant: (merchantId: string) => void;
  refreshMerchantAccess: () => Promise<void>;
  /** Child-only multi-parent: set selected parent and leave parent selection (no legacy full child list fetch). */
  selectParentAndLoadChildren: (parent: MerchantAccess) => void;
  /** Child-only multi-parent: return to parent list view */
  goBackToParentMerchantSelection: () => void;
  /** Single-page fetch for GET /merchant-accounts/:userId/merchants (no automatic multi-page loading). */
  getPaginatedAccountMerchants: (
    params?: GetMerchantsByUserParams,
  ) => Promise<PaginatedResponse<MerchantAccess>>;
  canAccess: (permission: AuthPermission) => boolean;
  canUpdateMerchantSettings: () => boolean;
  canUpdateAgentHub: () => boolean;
  hasMerchantAccess: () => boolean;
  hasAgentHubAccess: () => boolean;
  hasPaylinkAccess: () => boolean;
  hasCardOnFileAccess: () => boolean;
  hasInsightsAccess: () => boolean;
  getHomePath: () => IndexPath;
  isSkipifyAdmin: boolean;
  isSkipifyUser: boolean;
  isChildMerchantOnlyUser: boolean;
  isChildMerchantRestrictedShell: boolean;
  unauthorizedMerchantId: boolean;
};

const MerchantContext = createContext<MerchantContextValue | null>(null);

export const MerchantProvider: React.FC<
  React.PropsWithChildren<{
    initialState?: Partial<MerchantContextState>;
  }>
> = ({ children, initialState: initialStateProp }) => {
  const initialState = useMemo(
    () => ({ ...defaultState, ...initialStateProp }),
    [initialStateProp],
  );
  const [state, setState] = useState<MerchantContextState>(initialState);

  // Use Auth0 hook to get authentication state and functions
  const {
    isAuthenticated,
    logout: auth0Logout,
    getAccessTokenSilently,
    isLoading: auth0Loading,
  } = useAuth0();

  const [getMerchantsByUser] = useLazyGetMerchantsByUserQuery();
  const [getPrivateMerchant] = useLazyGetMerchantQuery();
  const [getPublicMerchant] = useLazyGetPublicMerchantQuery();
  const [searchParams, setSearchParams] = useSearchParams();

  const getPaginatedAccountMerchants = useCallback(
    async (params?: GetMerchantsByUserParams) => {
      if (!getAccessTokenSilently) {
        throw new Error('Not authenticated');
      }
      const token = await getAccessTokenWithAudience(getAccessTokenSilently);
      const userId = jwtDecode<DecodedJwtPayload>(token).sub;
      if (!userId) {
        throw new Error('Invalid token: missing subject');
      }
      return getMerchantsByUser({
        userId,
        token,
        params,
      }).unwrap();
    },
    [getAccessTokenSilently, getMerchantsByUser],
  );

  const merchantId = useMemo(() => {
    const fromWindow = readMerchantIdFromWindowSearch();
    if (fromWindow) return fromWindow;
    const q = searchParams.get('merchantId')?.trim();
    if (q && q.length <= 128) return q;
    return null;
  }, [searchParams]);

  // Update LaunchDarkly context whenever merchantId changes
  useUpdateLDContext('merchant', merchantId);

  const getSkipifyUserAccess = useCallback(
    async (userId: string, token: string, storedMerchantId: string | null) => {
      if (storedMerchantId) {
        // Check if the merchant has access to the stored merchantId
        try {
          const response = await getMerchantsByUser({
            userId,
            token,
            params: { merchantId: storedMerchantId },
          }).unwrap();
          return response.data[0];
        } catch (error) {
          Logger.warn(
            'User does not have access to the stored merchantId. Defaulting to first merchant.',
            error,
          );
        }
      }

      const firstMerchantResponse = await getMerchantsByUser({
        userId,
        token,
        params: { pageNumber: 1, pageSize: 1 },
      }).unwrap();

      return firstMerchantResponse.data[0];
    },
    [getMerchantsByUser],
  );

  const fetchMerchantsByUserAllPages = useCallback(
    async (
      userId: string,
      token: string,
      params: GetMerchantsByUserQuery['params'],
    ): Promise<MerchantAccess[]> => {
      const pageSize = params?.pageSize ?? 250;
      let pageNumber = params?.pageNumber ?? 1;
      let allMerchants: MerchantAccess[] = [];
      let totalPages = 1;

      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await getMerchantsByUser(
          {
            userId,
            token,
            params: { ...params, pageSize, pageNumber },
          },
          true,
        ).unwrap();
        allMerchants = [...allMerchants, ...response.data];
        totalPages = response.metadata.totalPages;
        pageNumber += 1;
      } while (pageNumber <= totalPages);

      return allMerchants;
    },
    [getMerchantsByUser],
  );

  const getMerchantUserAccess = useCallback(
    async (userId: string, token: string) => {
      return fetchMerchantsByUserAllPages(userId, token, { pageNumber: 1, pageSize: 250 });
    },
    [fetchMerchantsByUserAllPages],
  );

  /** First page of parents only — full parent lists use GET .../merchants from configuration views. */
  const fetchParentsFirstPageChildOnly = useCallback(
    async (
      userId: string,
      token: string,
    ): Promise<{ parents: MerchantAccess[]; totalParents: number }> => {
      const res = await getMerchantsByUser({
        userId,
        token,
        params: {
          pageNumber: 1,
          pageSize: CONFIG_MERCHANT_LIST_PAGE_SIZE,
          onlyParentMerchants: true,
        },
      }).unwrap();
      return {
        parents: dedupeMerchantAccess(res.data),
        totalParents: res.metadata.totalRecords,
      };
    },
    [getMerchantsByUser],
  );

  const getMerchant = useCallback(
    async (
      merchantId: string,
      merchantAccess?: MerchantAccess[] | null,
      userPermissions?: AuthPermission[] | null,
    ): Promise<{
      merchantId: string;
      name: string;
      directLineage: string[];
      partnerMerchantId: string | null;
      hasDescendants: boolean;
    }> => {
      // Check if user has merchant access using the provided permissions
      const hasAdminAccess = (userPermissions ?? []).some((p) =>
        p.startsWith(AuthPermissions.ADMIN_ALL),
      );
      const merchantPermissions: AuthPermission[] = [
        AuthPermissions.CREATE_MERCHANTS,
        AuthPermissions.READ_MERCHANTS,
        AuthPermissions.UPDATE_MERCHANTS,
        AuthPermissions.DELETE_MERCHANTS,
        AuthPermissions.UPDATE_CHILD_MERCHANTS_ONLY,
      ];
      const hasMerchantPermissions = (userPermissions ?? []).some((permission) =>
        merchantPermissions.includes(permission),
      );

      if (hasAdminAccess || hasMerchantPermissions) {
        const response = await getPrivateMerchant(merchantId, true).unwrap();
        return {
          merchantId: response.merchantId,
          name: response.name,
          directLineage: response.directLineage ?? [],
          partnerMerchantId: response.partnerMerchantId ?? null,
          hasDescendants: response.hasDescendants ?? false,
        };
      }

      // Paylink only users will not have access to private merchant data
      const response = await getPublicMerchant(merchantId, true).unwrap();
      const accessMerchantObj = merchantAccess?.find((m) => m.merchantId === merchantId);
      return {
        merchantId: response.merchantId,
        name:
          accessMerchantObj?.name ??
          response.branding?.displayName ??
          response.parentMerchantName ??
          '',
        directLineage: [],
        partnerMerchantId: accessMerchantObj?.partnerMerchantId ?? null,
        hasDescendants: false,
      };
    },
    [getPrivateMerchant, getPublicMerchant],
  );

  const getMerchantAccess = useCallback(
    async (
      token?: string,
    ): Promise<{
      selectedMerchantId: string | null;
      merchantAccess: MerchantAccess[] | null;
      parentMerchants: MerchantAccess[] | null;
      parentMerchantsTotalRecords: number | null;
      requiresParentSelection: boolean;
    }> => {
      setState((prev) => ({ ...prev, error: null }));
      // Get token from Auth0 if not provided
      const authToken =
        token ??
        (getAccessTokenSilently ? await getAccessTokenWithAudience(getAccessTokenSilently) : '');

      if (!authToken) {
        return {
          selectedMerchantId: null,
          merchantAccess: null,
          parentMerchants: null,
          parentMerchantsTotalRecords: null,
          requiresParentSelection: false,
        };
      }

      const userId = jwtDecode<DecodedJwtPayload>(authToken).sub;
      if (!userId) {
        return {
          selectedMerchantId: null,
          merchantAccess: null,
          parentMerchants: null,
          parentMerchantsTotalRecords: null,
          requiresParentSelection: false,
        };
      }

      let selectedMerchantAccess: MerchantAccess | null = null;
      let merchantAccess: MerchantAccess[] | null = null;
      let parentMerchants: MerchantAccess[] | null = null;
      let parentMerchantsTotalRecords: number | null = null;
      let requiresParentSelection = false;
      const storedMerchantId =
        readMerchantIdFromWindowSearch() ?? merchantId ?? localStorage.getItem('merchantId');

      try {
        const decodedToken = jwtDecode<DecodedJwtPayload>(authToken);
        const isSkipifyUser = (decodedToken.permissions ?? []).some(
          (permission) =>
            permission.startsWith(AuthPermissions.ADMIN_ALL) ||
            permission.startsWith(AuthPermissions.READ_ALL),
        );

        if (isSkipifyUser) {
          selectedMerchantAccess = await getSkipifyUserAccess(userId, authToken, storedMerchantId);
        } else if (isChildMerchantOnlyPermissionSet(decodedToken.permissions)) {
          const { parents: dedupedParents, totalParents } = await fetchParentsFirstPageChildOnly(
            userId,
            authToken,
          );
          parentMerchants = dedupedParents;
          parentMerchantsTotalRecords = totalParents;

          if (totalParents === 0) {
            return {
              selectedMerchantId: null,
              merchantAccess: null,
              parentMerchants: null,
              parentMerchantsTotalRecords: null,
              requiresParentSelection: false,
            };
          }

          const resolveParentRow = async (
            parentId: string,
          ): Promise<MerchantAccess | undefined> => {
            const fromPage = dedupedParents.find((p) => p.merchantId === parentId);
            if (fromPage) return fromPage;
            const scoped = await getMerchantsByUser({
              userId,
              token: authToken,
              params: {
                onlyParentMerchants: true,
                merchantId: parentId,
                pageNumber: 1,
                pageSize: 1,
              },
            }).unwrap();
            return scoped.data[0];
          };

          let storedAsParent = storedMerchantId
            ? dedupedParents.find((m) => m.merchantId === storedMerchantId)
            : undefined;
          if (storedMerchantId && !storedAsParent) {
            const scoped = await getMerchantsByUser({
              userId,
              token: authToken,
              params: {
                onlyParentMerchants: true,
                merchantId: storedMerchantId,
                pageNumber: 1,
                pageSize: 1,
              },
            }).unwrap();
            // Must match id: scoped.data[0] can be a parent when merchantId is a child, which would
            // skip GET /merchants/:id and leave multi-parent users stuck on the parent picker.
            storedAsParent = scoped.data.find((m) => m.merchantId === storedMerchantId);
          }

          if (storedMerchantId && !storedAsParent) {
            try {
              const privateMerchant = await getPrivateMerchant(storedMerchantId, true).unwrap();
              const partnerId = privateMerchant.partnerMerchantId ?? null;
              const childAccess: MerchantAccess = {
                merchantId: storedMerchantId,
                name: privateMerchant.name,
                partnerMerchantId: partnerId,
              };
              if (partnerId) {
                const parentRow = await resolveParentRow(partnerId);
                merchantAccess = parentRow
                  ? dedupeMerchantAccess([parentRow, childAccess])
                  : [childAccess];
              } else {
                merchantAccess = [childAccess];
              }
              // URL points at a child: keep selection on the child so initializeMerchantAuth
              // does not treat this as parent-picker mode and strip ?merchantId= (e.g. Login opens new tab).
              selectedMerchantAccess = childAccess;
              requiresParentSelection = false;
            } catch {
              // fall through — try public merchant for deep link (private GET may fail for some children)
            }
          }

          if (!selectedMerchantAccess && storedMerchantId && !storedAsParent) {
            try {
              const pub = await getPublicMerchant(storedMerchantId, true).unwrap();
              const childAccess: MerchantAccess = {
                merchantId: storedMerchantId,
                name: pub.branding?.displayName ?? pub.parentMerchantName ?? storedMerchantId,
                partnerMerchantId: pub.topLevelMerchantId ?? null,
              };
              merchantAccess = [childAccess];
              selectedMerchantAccess = childAccess;
              requiresParentSelection = false;
            } catch {
              // fall through to single- vs multi-parent resolution below
            }
          }

          if (!selectedMerchantAccess) {
            if (totalParents === 1) {
              const fullList = await fetchMerchantsByUserAllPages(userId, authToken, {
                pageSize: CHILD_ONLY_LIST_PAGE_SIZE,
              });
              const dedupedFull = dedupeMerchantAccess(fullList);
              merchantAccess = dedupedFull;
              const storedMerchantAccessSingle =
                storedMerchantId && dedupedFull.find((m) => m.merchantId === storedMerchantId);
              if (storedMerchantAccessSingle) {
                selectedMerchantAccess = storedMerchantAccessSingle;
              } else {
                selectedMerchantAccess =
                  dedupedFull.find((m) => m.merchantId === dedupedParents[0]?.merchantId) ??
                  dedupedParents[0];
              }
              requiresParentSelection = false;
            } else {
              merchantAccess = dedupedParents;
              requiresParentSelection = true;
              selectedMerchantAccess = null;
            }
          }
        } else {
          merchantAccess = await getMerchantUserAccess(userId, authToken);

          merchantAccess = dedupeMerchantAccess(merchantAccess);

          const localStorageMerchantId = localStorage.getItem('merchantId');
          const urlMerchantNotAccessible =
            !!merchantId && !merchantAccess.find((m) => m.merchantId === merchantId);
          const localStorageMerchantAccessible =
            !!localStorageMerchantId &&
            !!merchantAccess.find((m) => m.merchantId === localStorageMerchantId);

          if (urlMerchantNotAccessible && !localStorageMerchantAccessible) {
            return {
              selectedMerchantId: null,
              merchantAccess,
              parentMerchants: null,
              parentMerchantsTotalRecords: null,
              requiresParentSelection: false,
            };
          }

          selectedMerchantAccess = merchantAccess[0];

          const preferredMerchantId = urlMerchantNotAccessible
            ? localStorageMerchantId
            : storedMerchantId;

          const storedMerchantAccess =
            preferredMerchantId && merchantAccess.find((m) => m.merchantId === preferredMerchantId);
          if (storedMerchantAccess) {
            selectedMerchantAccess = storedMerchantAccess;
          }
        }
      } catch (error) {
        Logger.error('Error getting merchant access', error);
        setState((prev) => ({
          ...prev,
          error: error as Error,
        }));
      }

      return {
        selectedMerchantId: selectedMerchantAccess?.merchantId ?? null,
        merchantAccess,
        parentMerchants,
        parentMerchantsTotalRecords,
        requiresParentSelection,
      };
    },
    [
      getAccessTokenSilently,
      merchantId,
      getSkipifyUserAccess,
      getMerchantUserAccess,
      fetchMerchantsByUserAllPages,
      fetchParentsFirstPageChildOnly,
      getMerchantsByUser,
      getPrivateMerchant,
      getPublicMerchant,
    ],
  );

  const getDirectLineage: (
    merchantIds: string[],
    merchantAccess: MerchantAccess[] | null,
    userPermissions?: AuthPermission[] | null,
    parentMerchants?: MerchantAccess[] | null,
  ) => Promise<LineageItem[]> = useCallback(
    async (
      merchantIds: string[],
      merchantAccess: MerchantAccess[] | null,
      userPermissions?: AuthPermission[] | null,
      parentMerchants?: MerchantAccess[] | null,
    ) =>
      Promise.all(
        (merchantIds ?? []).map(async (merchantId) => {
          // First, check if we have the merchant in the merchantAccess array
          const merchant = merchantAccess?.find((m) => m.merchantId === merchantId);
          if (merchant) {
            return {
              name: merchant.name,
              merchantId: merchant.merchantId,
              hasAccess: true,
            };
          }

          const perms = userPermissions ?? [];
          if (isChildMerchantOnlyPermissionSet(perms)) {
            const hasAccess = isChildOnlyLineageMerchantNavigable(
              merchantIds ?? [],
              merchantId,
              merchantAccess,
              parentMerchants ?? null,
            );
            try {
              const response = await getPublicMerchant(merchantId, true).unwrap();
              const name = response.branding?.displayName ?? response.parentMerchantName ?? '';
              return {
                merchantId: response.merchantId,
                name,
                hasAccess,
              };
            } catch {
              return {
                merchantId,
                name: '',
                hasAccess: false,
              };
            }
          }

          const hasMerchantPermissions = perms.some((p) => MERCHANT_PERMISSIONS.includes(p));
          const hasLineageNavigateAccess = isMerchantScopedLineageMerchantNavigable(
            merchantId,
            merchantAccess,
            perms,
          );

          // merchantIds come from GET /merchants/:id?include=configs → directLineage (IDs only).
          // Resolve names like getMerchant: private when allowed, else public for display name only.
          if (hasLineageNavigateAccess || hasMerchantPermissions) {
            try {
              const response = await getPrivateMerchant(merchantId, true).unwrap();
              return {
                merchantId: response.merchantId,
                name: response.name,
                hasAccess: hasLineageNavigateAccess,
              };
            } catch {
              // fall through — e.g. intermediate ancestor private GET not permitted
            }
          }

          const response = await getPublicMerchant(merchantId, true).unwrap();

          return {
            merchantId: response.merchantId,
            name: response.branding?.displayName ?? response.parentMerchantName ?? '',
            hasAccess: hasLineageNavigateAccess,
          };
        }),
      ),
    [getPrivateMerchant, getPublicMerchant],
  );

  const logout = useCallback(() => {
    // Clear business logic data from localStorage
    localStorage.removeItem('merchantId');
    localStorage.removeItem('emailAddress');
    localStorage.removeItem('merchantUserId');

    // Use Auth0's logout - exactly as documented
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [auth0Logout]);

  const setMerchant = useCallback(
    (newMerchantId: string) => {
      // Updating the URL search params will trigger the useEffect below
      // which will fetch the new merchant and update the state.
      // This is not done directly here because we also need to handle cases like the user clicking
      // the back button on the browser.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('merchantId', newMerchantId);
        return next;
      });
    },
    [setSearchParams],
  );

  const fetchNewMerchantAndUpdateState = useCallback(
    async (merchantId: string) => {
      setState((prev) => ({ ...prev, isLoading: true, unauthorizedMerchantId: false }));

      try {
        const merchantAccess = state.merchantAccess;
        const merchant = await getMerchant(merchantId, merchantAccess, state.permissions);
        const directLineage = await getDirectLineage(
          merchant.directLineage,
          state.merchantAccess,
          state.permissions,
          state.parentMerchants,
        );
        setState((prev) => {
          const leaveChildLanding =
            prev.pendingChildMerchantLandingAfterParentPick &&
            prev.parentMerchants != null &&
            !prev.parentMerchants.some((p) => p.merchantId === merchantId);

          return {
            ...prev,
            merchantName: merchant.name,
            directLineage,
            partnerMerchantId: merchant.partnerMerchantId,
            hasDescendants: merchant.hasDescendants,
            isLoading: false,
            ...(leaveChildLanding ? { pendingChildMerchantLandingAfterParentPick: false } : {}),
          };
        });
        localStorage.setItem('merchantId', merchantId);
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error as Error,
        }));
      }
    },
    [state.merchantAccess, state.permissions, state.parentMerchants, getMerchant, getDirectLineage],
  );

  // Update the merchant state when the merchantId in the URL changes
  useEffect(() => {
    if (!merchantId || !state.permissions?.length) return;
    // Avoid fetching a URL merchant while the child-only parent picker is active; otherwise we
    // briefly resolve the parent name before the URL is cleared and overwrite merchantName: ''.
    if (state.requiresParentSelection) return;
    fetchNewMerchantAndUpdateState(merchantId);
  }, [
    fetchNewMerchantAndUpdateState,
    merchantId,
    state.permissions,
    state.requiresParentSelection,
  ]);

  const initializeMerchantAuth = useCallback(async () => {
    if (!isAuthenticated || !getAccessTokenSilently) {
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true }));

    let decodedJWT: DecodedJwtPayload;
    try {
      const token = await getAccessTokenWithAudience(getAccessTokenSilently);
      decodedJWT = jwtDecode<DecodedJwtPayload>(token);
    } catch (error) {
      Logger.error('Error initializing merchant auth', error);
      setState((prev) => ({ ...prev, isLoading: false, error: error as Error }));
      return;
    }

    const { permissions } = decodedJWT;
    if (!permissions || permissions.length === 0) {
      logout();
      return;
    }

    configureAuth();

    const {
      selectedMerchantId,
      merchantAccess,
      parentMerchants,
      parentMerchantsTotalRecords,
      requiresParentSelection,
    } = await getMerchantAccess();
    // If the user does not have access to any merchants, log them out.
    if ((!merchantAccess || merchantAccess.length === 0) && !selectedMerchantId) {
      logout();
      return;
    }

    // If the user does have access to merchants, but the merchantId is not in the merchantAccess
    // array, then the merchantId is unauthorized. This will redirect the user to the unauthorized
    // merchant page.
    const parentPickerCount = parentMerchantsTotalRecords ?? parentMerchants?.length ?? 0;
    const awaitingParentSelection =
      requiresParentSelection && parentPickerCount > 0 && !selectedMerchantId;
    if (!selectedMerchantId && !awaitingParentSelection) {
      setState((prev) => ({ ...prev, unauthorizedMerchantId: true, isLoading: false }));
      return;
    }

    setState((prev) => ({
      ...prev,
      merchantUserId: decodedJWT.sub ?? null,
      emailAddress: decodedJWT.email,
      error: null,
      permissions,
      merchantAccess,
      parentMerchants,
      parentMerchantsTotalRecords,
      requiresParentSelection,
      ...(awaitingParentSelection
        ? {
            merchantName: '',
            directLineage: null,
            partnerMerchantId: undefined,
            pendingChildMerchantLandingAfterParentPick: false,
            unauthorizedMerchantId: false,
            isLoading: false,
          }
        : {}),
    }));

    localStorage.setItem('emailAddress', decodedJWT.email);
    localStorage.setItem('merchantUserId', decodedJWT.sub ?? '');

    if (awaitingParentSelection) {
      const idInUrl = readMerchantIdFromWindowSearch();
      const urlPointsAtKnownParent =
        !!idInUrl && !!parentMerchants?.some((p) => p.merchantId === idInUrl);
      // Only clear ?merchantId= when it refers to a parent row (picker should start without a selection).
      // Never strip child / deep-link ids — that removes Login-as-child URLs.
      if (!idInUrl || urlPointsAtKnownParent) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('merchantId');
            return next;
          },
          { replace: true },
        );
        localStorage.removeItem('merchantId');
      }
      return;
    }

    const shouldUpdateMerchant = !!selectedMerchantId && selectedMerchantId !== merchantId;
    if (shouldUpdateMerchant) {
      setMerchant(selectedMerchantId);
    }
  }, [
    getAccessTokenSilently,
    getMerchantAccess,
    isAuthenticated,
    logout,
    merchantId,
    setMerchant,
    setSearchParams,
  ]);

  // Initialize merchant auth data when Auth0 authentication is complete.
  // After Continue on the parent grid, React state clears parent-picker mode before the URL always
  // reflects the new merchantId; without the merchantUserId guard, this effect would call
  // initializeMerchantAuth again and undo progress.
  useEffect(() => {
    const needsBootstrap = !state.permissions;
    const needsMerchantBeforeBootstrap =
      !merchantId && !state.requiresParentSelection && state.merchantUserId == null;
    if (isAuthenticated && !auth0Loading && (needsBootstrap || needsMerchantBeforeBootstrap)) {
      initializeMerchantAuth();
    }
  }, [
    isAuthenticated,
    auth0Loading,
    initializeMerchantAuth,
    state.permissions,
    merchantId,
    state.requiresParentSelection,
    state.merchantUserId,
  ]);

  const selectParentAndLoadChildren = useCallback(
    (parent: MerchantAccess) => {
      flushSync(() => {
        setState((prev) => ({
          ...prev,
          merchantAccess: [parent],
          requiresParentSelection: false,
          pendingChildMerchantLandingAfterParentPick: true,
          isLoading: false,
        }));
        setMerchant(parent.merchantId);
      });
    },
    [setMerchant],
  );

  const goBackToParentMerchantSelection = useCallback(() => {
    const parents = state.parentMerchants;
    if (!parents?.length) return;

    setState((prev) => ({
      ...prev,
      merchantAccess: parents,
      requiresParentSelection: true,
      pendingChildMerchantLandingAfterParentPick: false,
      merchantName: '',
      directLineage: null,
      partnerMerchantId: undefined,
    }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('merchantId');
        return next;
      },
      { replace: true },
    );
    localStorage.removeItem('merchantId');
  }, [state.parentMerchants, setSearchParams]);

  const refreshMerchantAccess = useCallback(async () => {
    if (!getAccessTokenSilently) return;
    const authToken = await getAccessTokenWithAudience(getAccessTokenSilently);
    const decoded = jwtDecode<DecodedJwtPayload>(authToken);
    const userId = decoded.sub;
    if (!userId) return;

    if (isChildMerchantOnlyPermissionSet(decoded.permissions)) {
      const { parents, totalParents } = await fetchParentsFirstPageChildOnly(userId, authToken);
      setState((prev) => {
        if (prev.requiresParentSelection) {
          return {
            ...prev,
            parentMerchants: parents,
            parentMerchantsTotalRecords: totalParents,
            merchantAccess: parents,
            pendingChildMerchantLandingAfterParentPick: false,
          };
        }
        return { ...prev, parentMerchants: parents, parentMerchantsTotalRecords: totalParents };
      });
      return;
    }

    const { merchantAccess: nextAccess } = await getMerchantAccess(authToken);
    if (nextAccess) {
      setState((prev) => ({ ...prev, merchantAccess: nextAccess }));
    }
  }, [getAccessTokenSilently, getMerchantAccess, fetchParentsFirstPageChildOnly]);

  // Permission checking functions
  const canAccess = useCallback(
    (permission: AuthPermission) => {
      return checkCanAccess(permission, state.permissions ?? []);
    },
    [state.permissions],
  );

  const canUpdateMerchantSettings = useCallback(() => {
    const permissions = state.permissions ?? [];
    if (checkCanAccess(AuthPermissions.UPDATE_MERCHANTS, permissions)) {
      return true;
    }
    if (checkCanAccess(AuthPermissions.UPDATE_CHILD_MERCHANTS_ONLY, permissions)) {
      const pid = state.partnerMerchantId;
      return typeof pid === 'string' && pid.length > 0;
    }
    return false;
  }, [state.permissions, state.partnerMerchantId]);

  const canUpdateAgentHub = useCallback(() => {
    return checkCanAccess(AuthPermissions.UPDATE_AGENT_HUB, state.permissions ?? []);
  }, [state.permissions]);

  const hasMerchantAccess = useCallback(() => {
    const permissions = state.permissions ?? [];
    return MERCHANT_PERMISSIONS.some((permission) => checkCanAccess(permission, permissions));
  }, [state.permissions]);

  const hasAgentHubAccess = useCallback(() => {
    const permissions = state.permissions ?? [];
    return AGENT_HUB_PERMISSIONS.some((permission) => checkCanAccess(permission, permissions));
  }, [state.permissions]);

  const hasPaylinkAccess = useCallback(() => {
    const permissions = state.permissions ?? [];
    return PAYLINK_PERMISSIONS.some((permission) => checkCanAccess(permission, permissions));
  }, [state.permissions]);

  const hasCardOnFileAccess = useCallback(() => {
    const permissions = state.permissions ?? [];
    return CARD_ON_FILE_PERMISSIONS.some((permission) => checkCanAccess(permission, permissions));
  }, [state.permissions]);

  const hasInsightsAccess = useCallback(() => {
    const permissions = state.permissions ?? [];
    return INSIGHTS_PERMISSIONS.some((permission) => checkCanAccess(permission, permissions));
  }, [state.permissions]);

  const getHomePath: () => IndexPath = useCallback(() => {
    if (hasMerchantAccess()) {
      return `/${tabs.CONFIGURATION}`;
    }

    if (hasPaylinkAccess()) {
      return `/${tabs.PAYLINKS}`;
    }

    if (hasCardOnFileAccess()) {
      return `/${tabs.CARD_ON_FILE}`;
    }

    if (hasInsightsAccess()) {
      return `/${tabs.INSIGHTS}`;
    }

    if (hasAgentHubAccess()) {
      return `/${tabs.AGENT_HUB}`;
    }

    return `/${tabs.PAYLINKS}`;
  }, [
    hasMerchantAccess,
    hasPaylinkAccess,
    hasCardOnFileAccess,
    hasInsightsAccess,
    hasAgentHubAccess,
  ]);

  const isSkipifyAdminValue = useMemo(() => {
    return (state.permissions ?? []).some((permission) =>
      permission.startsWith(AuthPermissions.ADMIN_ALL),
    );
  }, [state.permissions]);

  const isSkipifyUserValue = useMemo(() => {
    return (state.permissions ?? []).some(
      (permission) =>
        permission.startsWith(AuthPermissions.ADMIN_ALL) ||
        permission.startsWith(AuthPermissions.READ_ALL),
    );
  }, [state.permissions]);

  const isChildMerchantOnlyUser = useMemo(() => {
    const permissions = state.permissions ?? [];
    return (
      checkCanAccess(AuthPermissions.UPDATE_CHILD_MERCHANTS_ONLY, permissions) &&
      !checkCanAccess(AuthPermissions.UPDATE_MERCHANTS, permissions)
    );
  }, [state.permissions]);

  /** URL merchant is a child-only parent-picker row (shared with Configuration / Nav). */
  const merchantIdIsParentPickerRow = isMerchantIdParentPickerRow(
    merchantId,
    state.parentMerchants,
  );

  /**
   * Child-only: hide Paylinks / merchant switcher until acting inside a child shop. Stays on while
   * partner is unresolved, on parent-picker URL, or pending after Continue (matches Configuration).
   */
  const isChildMerchantRestrictedShell = useMemo(() => {
    if (!isChildMerchantOnlyUser) return false;
    const p = state.partnerMerchantId;
    if (p === undefined || p === null || p === '') return true;
    if (state.requiresParentSelection) return false;
    return state.pendingChildMerchantLandingAfterParentPick || merchantIdIsParentPickerRow;
  }, [
    isChildMerchantOnlyUser,
    state.partnerMerchantId,
    state.requiresParentSelection,
    state.pendingChildMerchantLandingAfterParentPick,
    merchantIdIsParentPickerRow,
  ]);

  useEffect(() => {
    if (!isChildMerchantOnlyUser || !state.requiresParentSelection) return;
    if (!searchParams.has('merchantId')) return;
    const urlMerchantId = searchParams.get('merchantId');
    const isKnownParent =
      !!urlMerchantId && !!state.parentMerchants?.some((p) => p.merchantId === urlMerchantId);
    // Only strip when the query points at a parent row we have in state (stray parent id during picker).
    // If parentMerchants is still empty, or the id is a child, do not strip — avoids wiping Login deep links.
    if (!isKnownParent) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('merchantId');
        return next;
      },
      { replace: true },
    );
  }, [
    isChildMerchantOnlyUser,
    state.requiresParentSelection,
    state.parentMerchants,
    searchParams,
    setSearchParams,
  ]);

  const value = useMemo(
    () => ({
      ...state,
      merchantId: merchantId ?? '', // AuthenticatedRouteGuard will handle cases where merchantId is null
      logout,
      setMerchant,
      refreshMerchantAccess,
      selectParentAndLoadChildren,
      goBackToParentMerchantSelection,
      getPaginatedAccountMerchants,
      canAccess,
      canUpdateMerchantSettings,
      canUpdateAgentHub,
      hasMerchantAccess,
      hasAgentHubAccess,
      hasPaylinkAccess,
      hasCardOnFileAccess,
      hasInsightsAccess,
      getHomePath,
      isSkipifyAdmin: isSkipifyAdminValue,
      isSkipifyUser: isSkipifyUserValue,
      isChildMerchantOnlyUser,
      isChildMerchantRestrictedShell,
    }),
    [
      state,
      merchantId,
      logout,
      setMerchant,
      refreshMerchantAccess,
      selectParentAndLoadChildren,
      goBackToParentMerchantSelection,
      getPaginatedAccountMerchants,
      canAccess,
      canUpdateMerchantSettings,
      canUpdateAgentHub,
      hasMerchantAccess,
      hasAgentHubAccess,
      hasPaylinkAccess,
      hasCardOnFileAccess,
      hasInsightsAccess,
      getHomePath,
      isSkipifyAdminValue,
      isSkipifyUserValue,
      isChildMerchantOnlyUser,
      isChildMerchantRestrictedShell,
    ],
  );

  return <MerchantContext.Provider value={value}>{children}</MerchantContext.Provider>;
};

export const useMerchant = () => {
  const context = useContext(MerchantContext);
  if (!context) {
    throw new Error('useMerchant must be used within a MerchantProvider');
  }
  return context;
};
